import { useState } from "react";
import { PaperPlaneRight, Robot, Spinner, Sparkle } from "@phosphor-icons/react";
import { api, fmtErr } from "../../lib/api";
import { toast } from "sonner";

// Embedded copilot for the Drawing Studio: general chat via /copilot/chat with drawing context,
// drawing_change actions are routed to /drawings/{id}/ai-modify → ghost preview on canvas.
export default function StudioCopilot({ projectId, drawing, onOps }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [prog, setProg] = useState(null);

  const enrich = async () => {
    if (!drawing || enriching) return;
    setEnriching(true);
    setProg({ percent: 2, step: "Queueing engineering pass…" });
    setMessages((m) => [...m, { role: "user", content: "Enrich this sheet to manufacturing grade" }]);
    try {
      const { data: started } = await api.post(`/drawings/${drawing.id}/enrich`);
      let data = null;
      for (let i = 0; i < 400; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        const { data: job } = await api.get(`/engineering/jobs/${started.job_id}`);
        setProg({ percent: job.percent ?? 0, step: job.step || "Working…" });
        if (job.status === "done") { data = job.result; break; }
        if (job.status === "error") throw new Error(job.error || "Enrichment failed");
      }
      if (!data) throw new Error("Enrichment timed out");
      const c = data.counts || {};
      const lines = [
        `**Engineering package ready** — ${data.ops.length} annotation element(s) previewed as ghosts on the canvas. Apply or Cancel above the canvas.`,
        "",
        `· ${c.general_notes || 0} general notes  · ${c.gdt || 0} GD&T controls  · ${c.tolerances || 0} tolerances`,
        `· ${c.welds || 0} weld specs  · ${c.surface_finish || 0} finish specs  · ${c.materials || 0} materials`,
        `· ${c.clearances || 0} clearances  · ${c.testing || 0} tests  · ${c.handling_transport || 0} handling notes`,
        "",
        data.summary || "",
      ];
      onOps(data.ops);
      setMessages((m) => [...m, { role: "assistant", content: lines.join("\n") }]);
      toast.success("Manufacturing-grade annotations ready for review");
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `Enrichment failed: ${fmtErr(e)}` }]);
      toast.error(fmtErr(e), { duration: 8000 });
    } finally { setEnriching(false); setProg(null); }
  };

  const send = async () => {
    const msg = input.trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const ctx = drawing
        ? `[Drawing Studio: viewing "${drawing.name}" ${drawing.drawing_no} Rev ${drawing.rev_label || "0"}, ${drawing.status}] `
        : "[Drawing Studio: no drawing loaded] ";
      const { data } = await api.post("/copilot/chat", {
        project_id: projectId, message: ctx + msg, context_screen: "drawing-studio",
      }, { timeout: 120000 });
      let reply = data.reply || "";
      const MOD_VERBS = /\b(add|move|rotate|delete|remove|change|shift|place|insert|annotate)\b/i;
      const actType = data.action?.type || data.action?.action;
      const wantsModify = drawing && (actType === "drawing_change" || (!actType && MOD_VERBS.test(msg)));
      if (wantsModify) {
        try {
          const instr = actType === "drawing_change"
            ? `${msg}. ${(data.action.changes || [])
                .map((c) => `${c.operation} ${c.element} ${JSON.stringify(c.params || {})}`).join("; ")}`
            : msg;
          const res = await api.post(`/drawings/${drawing.id}/ai-modify`, { instruction: instr }, { timeout: 120000 });
          if (res.data.ops?.length) {
            onOps(res.data.ops);
            reply += `\n\n**Preview ready** — ${res.data.ops.length} operation(s) shown as blue ghosts on the canvas. Apply or Cancel above the canvas.`;
          } else {
            reply += `\n\n_${res.data.explanation || "I couldn't identify the elements to modify. Try being more specific (e.g., 'move the leftmost HV bushing')."}_`;
          }
        } catch (e) {
          reply += `\n\n_Could not build a preview: ${fmtErr(e)}_`;
        }
      }
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `Error: ${fmtErr(e)}` }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col h-full" data-testid="studio-copilot">
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-[var(--c-border)]">
        <Robot size={13} className="text-[var(--c-accent)]" />
        <span className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">Cognara AI</span>
        {drawing && <span className="ml-auto font-mono text-[9px] text-[var(--c-green)]">● {drawing.type}</span>}
      </div>
      <div className="px-2 pt-2">
        <button data-testid="enrich-drawing-btn" onClick={enrich} disabled={!drawing || enriching}
          title="Add GD&T, tolerances, weld and finish specs, fabrication/testing notes and standard references derived from this design"
          className="w-full h-7 rounded-sm border border-[var(--c-accent)]/50 bg-[var(--c-accent)]/10 text-[var(--c-accent)] text-[10px] flex items-center justify-center gap-1.5 hover:bg-[var(--c-accent)]/20 disabled:opacity-40 transition-colors duration-150">
          {enriching ? <Spinner size={11} className="animate-spin" /> : <Sparkle size={11} />}
          {enriching ? `Cognara AI · Engineering — ${prog?.percent ?? 0}%` : drawing ? "Enrich to manufacturing grade" : "Load a drawing to enrich it"}
        </button>
        {enriching && (
          <div className="mt-1.5" data-testid="enrich-progress">
            <div className="h-1.5 w-full bg-[var(--c-border)] rounded-sm overflow-hidden">
              <div data-testid="enrich-progress-bar"
                className="h-full bg-[var(--c-accent)] transition-[width] duration-500"
                style={{ width: `${Math.min(prog?.percent ?? 0, 100)}%` }} />
            </div>
            <div className="flex justify-between mt-1">
              <span data-testid="enrich-progress-step" className="text-[9px] text-[var(--c-muted)] truncate pr-2">
                {prog?.step || "Working…"}
              </span>
              <span className="text-[9px] font-mono text-[var(--c-accent)]">{prog?.percent ?? 0}%</span>
            </div>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-2" data-testid="copilot-messages">
        {messages.length === 0 && (
          <div className="text-[10px] text-[var(--c-muted)] leading-relaxed px-1">
            Ask about this drawing, or request a modification — e.g. "add a note: check oil level" — I'll preview changes as blue ghosts before applying.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`text-[11px] leading-relaxed rounded-sm px-2 py-1.5 whitespace-pre-wrap ${
            m.role === "user" ? "bg-[var(--c-accent)]/15 text-[var(--c-text)]" : "bg-[var(--c-border)]/40 text-[var(--c-text)]"}`}>
            {m.content}
          </div>
        ))}
        {busy && <div className="flex items-center gap-1.5 text-[10px] text-[var(--c-muted)]"><Spinner size={11} className="animate-spin" /> Thinking…</div>}
      </div>
      <div className="p-2 border-t border-[var(--c-border)] flex gap-1.5">
        <input data-testid="copilot-input" value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={drawing ? "Ask or request a drawing change…" : "Load a drawing to enable modifications"}
          className="flex-1 h-7 bg-[var(--c-bg)] border border-[var(--c-border)] text-[var(--c-text)] text-[11px] rounded-sm px-2 outline-none focus:border-[var(--c-accent)]" />
        <button data-testid="copilot-send" onClick={send} disabled={busy || !input.trim()}
          className="h-7 w-7 flex items-center justify-center rounded-sm bg-[var(--c-accent)] text-white disabled:opacity-40">
          <PaperPlaneRight size={12} />
        </button>
      </div>
    </div>
  );
}
